/* eslint-disable no-console */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import toast from 'react-hot-toast';
import {
  AuthState, Token, User, Persist,
} from 'features/login/types';
import { PURGE, REHYDRATE } from 'redux-persist';
import type { RootState } from 'common/store/store';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { setSentryUser, clearSentryUser } from 'common/utils/sentry';
import { setSharedPreference, removeSharedPreference } from 'common/utils/sharedPreferences';
import { clearPendingPath } from 'common/utils/pushRegistration';
import { clearSpotlight } from 'common/utils/spotlight';
import { clearWidgetSnapshot, resumeWidgetSnapshotWrites } from 'common/utils/widgetSnapshot';
import { wipeAll as wipeOfflineVault } from 'common/utils/offlineVault';
import { Mixpanel } from 'common/utils/mixpanel';

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null, token: null, persist: null, lastValidatedAt: null, shareToken: null,
  } as AuthState,
  reducers: {
    setCredentials: (
      state,
      {
        payload: {
          user, token, persist, shareToken,
        },
      }: PayloadAction<{ user: User | null; token: Token | null; persist: Persist | null, shareToken?: string | null }>,
    ) => {
      state.user = user;
      state.token = token;
      state.persist = persist;

      // Reopen the widget snapshot's write latch, which clearWidgetSnapshot
      // closes on every credential wipe. Without a reopen point, a user who
      // signed out and back in within the same JS session would get no
      // home-screen widget at all until the app was restarted. This is the
      // ONLY reopen point on purpose: token rotation dispatches `refreshToken`
      // below, not this reducer, so a 401-triggered clearCredentials racing a
      // concurrent successful request cannot reopen the latch it just closed.
      // See widgetSnapshot.ts's own comment for the full interleaving.
      resumeWidgetSnapshotWrites();

      // The scoped quick-save token only arrives on responses that were asked
      // for it (native validate/login). Absence means "no change", not
      // "revoke" — most setCredentials call sites never carry it.
      if (shareToken) {
        state.shareToken = shareToken;
        if (Capacitor.isNativePlatform()) {
          try {
            setSharedPreference('shareToken', shareToken);
          } catch (error) {
            console.error('Failed to set share token in Preferences:', error);
          }
        }
      }

      // Set user context in Sentry for error tracking
      if (user?.id) {
        setSentryUser({ id: user.id.toString(), email: user.email });
      }

      if (Capacitor.isNativePlatform() && token) {
        try {
          setSharedPreference('userToken', token.token);
          // toast.success('Token set');
        } catch (error) {
          console.error('Failed to set token in Preferences:', error);
          // toast.error('Failed to set token');
        }
      }
    },
    clearCredentials: (state) => {
      // Read before the wipe below: only a real sign-out resets analytics.
      // clearCredentials also runs for guests with no session (route guards),
      // and resetting them would cut their onboarding off from their sign-up.
      const wasSignedIn = Boolean(state.token?.token || state.user?.id || state.user?.user?.id);
      state.user = authSlice.getInitialState().user;
      state.token = authSlice.getInitialState().token;
      state.persist = authSlice.getInitialState().persist;
      state.lastValidatedAt = authSlice.getInitialState().lastValidatedAt;
      state.shareToken = authSlice.getInitialState().shareToken;

      // Drop any queued shortcut/push/universal-link path so it can't be
      // consumed by whichever DIFFERENT session logs in next on this
      // device (see pushRegistration.ts's clearPendingPath doc comment).
      clearPendingPath();

      // Clear user context from Sentry on logout
      clearSentryUser();

      // Purge the on-device Spotlight index so a signed-out user's document
      // names stop being searchable. MenuBar's onLogoutHandler also calls
      // clearSpotlight() directly, unconditionally and before the logout API
      // call even starts, so shared-device hygiene doesn't wait on (or
      // depend on the success of) that request; this dispatch only fires
      // once the API call resolves. Do not remove either call as
      // "redundant" - each covers a moment the other doesn't. No-op off
      // iOS; never throws.
      clearSpotlight();

      // Same reasoning as clearSpotlight immediately above, for the same
      // reason: a signed-out user's upcoming renewals must stop appearing
      // on the home-screen widget. See this call's other two sites
      // (MenuBar's onLogoutHandler, deleteUserAction's onQueryStarted) for
      // why all three are needed - do not remove any as "redundant". No-op
      // off iOS; never throws.
      clearWidgetSnapshot();

      // Destroys every document the user saved for offline use, and the
      // encryption key with them. This one matters more than the two above:
      // Spotlight and the widget leak names and dates, whereas the vault holds
      // the actual file contents. Same three-site reasoning - do not remove any
      // as "redundant". No-op off native; never throws.
      wipeOfflineVault();

      // Fresh anonymous Mixpanel id for whoever uses the device next, then
      // re-shared with RevenueCat (see Mixpanel.reset). Never throws.
      if (wasSignedIn) Mixpanel.reset();

      if (Capacitor.isNativePlatform()) {
        try {
          removeSharedPreference('userToken');
          removeSharedPreference('shareToken');
          // toast.success('Token removed');
        } catch (error) {
          console.error('Failed to remove token from Preferences:', error);
          // toast.error('Failed to remove token');
        }
      }
    },
    // Adopt a token that was minted elsewhere (another tab's persisted
    // rotation, or a newer persisted token discovered on a 401). Only the
    // token is touched — user/persist are unchanged — so this is safe to
    // dispatch from listeners/middleware that must not disturb the session.
    refreshToken: (
      state,
      {
        payload: { token },
      }: PayloadAction<{ token: Token }>,
    ) => {
      state.token = token;
      if (Capacitor.isNativePlatform() && token?.token) {
        setSharedPreference('userToken', token.token);
      }
    },
    // Stamp the time of a successful validate-token. Dispatched from both
    // Welcome and PersistLogin so PersistLogin can skip a redundant validate
    // that fires within seconds of Welcome's on the boot handoff.
    markValidated: (state) => {
      state.lastValidatedAt = Date.now();
    },
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, (state) => {
      state.user = authSlice.getInitialState().user;
      state.token = authSlice.getInitialState().token;
      state.persist = authSlice.getInitialState().persist;
      state.lastValidatedAt = authSlice.getInitialState().lastValidatedAt;
      state.shareToken = authSlice.getInitialState().shareToken;
      clearPendingPath();
      // Not currently dispatched anywhere in the app, but this is a full
      // credential wipe like clearCredentials above, so the same Spotlight
      // purge belongs here too as a safety net against a future PURGE call
      // site.
      clearSpotlight();
      // Same safety net as clearSpotlight immediately above, for the widget
      // snapshot: if a future PURGE ever fires (persistor.purge(), a
      // reset-app flow, corrupted-persist recovery), the home-screen widget
      // must not keep rendering the previous account's document names and
      // renewal dates with no session behind them.
      clearWidgetSnapshot();
      // Same safety net for the offline vault: a PURGE that left encrypted
      // document files and their key on disk would keep the previous account's
      // file contents readable by whoever signs in next.
      wipeOfflineVault();
    });
    // On every app cold-start, redux-persist rehydrates the auth slice
    // from local storage but doesn't go through setCredentials — so the
    // iOS App Group (used by the Share Extension) never gets refreshed.
    // Logged-in users would then see "Session expired" in the share
    // sheet because the extension was reading a stale token.
    // Force-sync the rehydrated token to the App Group here.
    builder.addMatcher(
      (action) => action.type === REHYDRATE,
      (state, action: { payload?: { auth?: AuthState } }) => {
        const restoredToken = action.payload?.auth?.token?.token;
        if (restoredToken && Capacitor.isNativePlatform()) {
          try {
            setSharedPreference('userToken', restoredToken);
          } catch (error) {
            console.error('Failed to sync token to App Group on rehydrate:', error);
          }
        }
        const restoredShareToken = action.payload?.auth?.shareToken;
        if (restoredShareToken && Capacitor.isNativePlatform()) {
          try {
            setSharedPreference('shareToken', restoredShareToken);
          } catch (error) {
            console.error('Failed to sync share token to App Group on rehydrate:', error);
          }
        }
      },
    );
  },
});

export const {
  setCredentials, clearCredentials, refreshToken, markValidated,
} = authSlice.actions;

export const getToken = async () => {
  try {
    const { value } = await Preferences.get({ key: 'userToken' });
    if (value) {
      // toast.success('Token retrieved successfully');
      return value;
    } else {
      // toast.error('Token not found');
      return null;
    }
  } catch (error) {
    console.error('Failed to retrieve token from Preferences:', error);
    // toast.error('Failed to retrieve token from Preferences');
    return null;
  }
};

export default authSlice.reducer;

export const selectCurrentUser = (state: RootState) => state.auth.user;
export const selectToken = (state: RootState) => state.auth.token;
export const selectLastValidatedAt = (state: RootState) => state.auth.lastValidatedAt;
